import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { Bell, Building2, LogOut, Shield, User, Users } from "lucide-react";
import { authClient } from "../../lib/auth-client";
import { Button } from "../../components/ui/button";
import { usePlatformAuth } from "../../lib/use-platform-auth";

const ALL_TABS = [
  { to: "profil", label: "Profil", icon: User, adminOnly: false },
  { to: "selskap", label: "Selskap", icon: Building2, adminOnly: true },
  { to: "brukere", label: "Brukere", icon: Users, adminOnly: true },
  { to: "varsler", label: "Varsler", icon: Bell, adminOnly: false },
  { to: "sikkerhet", label: "Sikkerhet", icon: Shield, adminOnly: false },
];

export function KontoPage() {
  const navigate = useNavigate();
  const { organizationSlug } = useParams<{ organizationSlug: string }>();
  const { organizations } = usePlatformAuth();

  const currentOrg = organizations.find((o) => o.slug === organizationSlug);
  const isAdminOrOwner = currentOrg && ["admin", "owner", "super_admin"].includes(currentOrg.role);
  const tabs = ALL_TABS.filter((tab) => !tab.adminOnly || isAdminOrOwner);

  const handleLogout = async () => {
    await authClient.signOut();
    navigate("/auth/login");
  };

  return (
    <div className="mx-auto max-w-screen-xl px-4 sm:px-6 py-4 sm:py-6">
      <header className="mb-8 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between animate-in fade-in slide-in-from-top-4 duration-700">
        <div>
          <h1
            className="text-4xl font-black text-[#1a2e35] tracking-tight"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Min konto
          </h1>
          <p className="mt-2 text-base font-medium text-muted-foreground max-w-2xl leading-relaxed">
            Oppdater din personlige profil, administrer organisasjonsinnstillinger og hold kontoen din trygg med passkeys.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            variant="secondary"
            size="lg"
            onClick={() => void handleLogout()}
            className="rounded-xl border-border bg-white shadow-sm hover:bg-muted/50 transition-all font-bold"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logg ut
          </Button>
        </div>
      </header>

      <div className="mb-8 overflow-x-auto">
        <div className="inline-flex h-auto items-center gap-1 rounded-2xl bg-muted/30 p-1.5 ring-1 ring-border/50">
          {tabs.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-black uppercase tracking-wider transition-all duration-200 whitespace-nowrap ${
                  isActive
                    ? "bg-white text-accent shadow-sm"
                    : "text-muted-foreground/70 hover:text-foreground"
                }`
              }
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </NavLink>
          ))}
        </div>
      </div>

      <Outlet />
    </div>
  );
}
